import { useEffect, useRef, useState } from 'react';
import { useNavigate, type HistoryState } from '@tanstack/react-router';
import { AlertTriangle, Clapperboard, FolderPlus, Loader2 } from 'lucide-react';
import { apiErrorMessage, captureService, type App } from '@/services/captureApi';
import { conversationAPI } from '@/services/api';
import { projectInitializationService } from '@/services/projectInitializationService';
import { useConversationStore } from '@/stores/conversationStore';
import { useUserStore } from '@/stores/userStore';
import { DEFAULT_MODEL } from '@/types/models';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { ProjectChoiceFields, type ProjectChoice } from './ProjectChoiceFields';

// [replay: anyway]: the user saw the replay check's reasons here and chose to build on it (the skill doesn't ask again).
const demoInstructions = (appId: string, anyway: boolean) => `/create-video-demo [app: ${appId}]${anyway ? ' [replay: anyway]' : ''}`;

type Step = 'publishing' | 'steps' | 'opening';

const STEP_LABEL: Record<Step, string> = { publishing: 'Publishing the app…', steps: 'Adding the steps…', opening: 'Opening the project…' };

function useCreateVideoProject() {
  const organization = useUserStore(s => s.activeOrganization);
  const team = useUserStore(s => s.activeTeam);
  const addConversation = useConversationStore(s => s.addConversation);
  return async (title: string) => {
    if (!organization || !team) throw new Error('Pick an organization and team first.');
    const { conversation } = await conversationAPI.create({
      title, model: DEFAULT_MODEL, organizationId: organization.id, teamId: team.id, collectionType: 'VIDEO',
    });
    addConversation(conversation);
    await projectInitializationService.initializeProject({ conversationId: conversation.id, initGit: true, retryOnGitError: true });
    return conversation.id;
  };
}

function useAbortOnUnmount() {
  const pending = useRef<AbortController | null>(null);
  useEffect(() => () => pending.current?.abort(), []);
  return () => (pending.current = new AbortController()).signal;
}

interface AppActionsProps {
  captureId: string;
  captureName: string;
  /** The steps that go into the project with the app: the capture's kept steps (none while they are being read). */
  stepIndices: number[];
  onError: (message: string | null) => void;
  /** The replay check's reasons when it said the replay needs a fix; null when it is ready. */
  needsFix?: string[] | null;
  onOpenReport?: () => void;
}

export function AppActions({ captureId, captureName, stepIndices, onError, needsFix = null, onOpenReport }: AppActionsProps) {
  const navigate = useNavigate();
  const createProject = useCreateVideoProject();
  const nextSignal = useAbortOnUnmount();
  const [step, setStep] = useState<Step | null>(null);
  const [adding, setAdding] = useState(false);
  const [asking, setAsking] = useState(false);

  async function startDemo(anyway = false) {
    if (needsFix && !anyway) { setAsking(true); return; }
    setAsking(false);
    const signal = nextSignal();
    onError(null);
    setStep('publishing');
    try {
      const app = anyway
        ? await captureService.publishAppAndWait(captureId, signal, { anyway: true })
        : await captureService.publishAppAndWait(captureId, signal);
      setStep('opening');
      const projectId = await createProject(captureName);
      if (stepIndices.length) await captureService.importToProject(captureId, { projectId, frameIndices: stepIndices });
      await captureService.attachApp(projectId, app.id);
      await navigate({
        to: '/chat/$conversationId/video-workspace',
        params: { conversationId: projectId },
        state: { autoSend: true, userInstructions: demoInstructions(app.id, anyway) } as HistoryState,
      });
    } catch (e) {
      if (signal.aborted) return;
      onError(apiErrorMessage(e));
      setStep(null);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setAdding(true)}
        disabled={!!step}
        className="h-7 inline-flex items-center gap-1.5 rounded-md px-2 font-medium text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50 disabled:pointer-events-none transition shrink-0"
      >
        <FolderPlus className="h-3.5 w-3.5" />
        Add to project
      </button>
      <button
        type="button"
        onClick={() => startDemo()}
        disabled={!!step}
        className={cn(
          'h-7 inline-flex items-center gap-1.5 rounded-md px-2.5 font-semibold transition shrink-0',
          'bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-70 disabled:cursor-wait',
        )}
      >
        {step ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Clapperboard className="h-3.5 w-3.5" />}
        {step ? STEP_LABEL[step] : 'Start a demo'}
      </button>
      {adding && (
        <AddToProjectDialog
          captureId={captureId}
          captureName={captureName}
          stepIndices={stepIndices}
          app="built"
          needsFix={needsFix}
          onClose={() => setAdding(false)}
        />
      )}
      {asking && needsFix && (
        <NeedsFixDialog
          reasons={needsFix}
          onReport={() => { setAsking(false); onOpenReport?.(); }}
          onAnyway={() => startDemo(true)}
          onClose={() => setAsking(false)}
        />
      )}
    </>
  );
}

function NeedsFixDialog({ reasons, onReport, onAnyway, onClose }: { reasons: string[]; onReport: () => void; onAnyway: () => void; onClose: () => void }) {
  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            This replay needs a fix
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          {reasons.length > 0 && (
            <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
              {reasons.map(r => <li key={r}>{r}</li>)}
            </ul>
          )}
          <p className="text-xs text-muted-foreground">
            A demo built on it shows these faults: an error screen, an empty answer, or data that's off. Recording the app
            again often fixes it.
          </p>
        </div>
        <DialogFooter className="gap-2">
          <button
            type="button"
            onClick={onReport}
            className="h-9 rounded-lg border border-border bg-card px-4 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition"
          >
            See the report
          </button>
          <button
            type="button"
            onClick={onAnyway}
            className="h-9 inline-flex items-center gap-1.5 rounded-lg px-4 text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition"
          >
            <Clapperboard className="h-3.5 w-3.5" />
            Build the demo anyway
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface AddToProjectDialogProps {
  captureId: string;
  captureName: string;
  /** The steps to add (frame indices); empty when the capture has none yet. */
  stepIndices: number[];
  /** built: the replay can be published and added as an app; building: not yet; unavailable: this server hosts no
   * replays; null: there is no replay. */
  app: 'built' | 'building' | 'unavailable' | null;
  needsFix?: string[] | null;
  onClose: () => void;
}

/**
 * Add to project puts both of a capture's planes in the project: its steps (screenshots and the elements on screen,
 * for screenshot scenes) and its replay as an app (for live scenes). The project's agents pick per scene.
 */
export function AddToProjectDialog({ captureId, captureName, stepIndices, app, needsFix = null, onClose }: AddToProjectDialogProps) {
  const navigate = useNavigate();
  const createProject = useCreateVideoProject();
  const nextSignal = useAbortOnUnmount();
  const conversations = useConversationStore(s => s.conversations);
  const projects = conversations.filter(c => c.collectionType === 'VIDEO');
  const [choice, setChoice] = useState<ProjectChoice>({ mode: 'new', name: captureName, projectId: projects[0]?.id ?? '' });
  const [withSteps, setWithSteps] = useState(stepIndices.length > 0);
  const [withApp, setWithApp] = useState(app === 'built');
  const [step, setStep] = useState<Step | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function add() {
    const name = choice.name.trim();
    if (choice.mode === 'new' && !name) return setError('Give the new project a name.');
    if (choice.mode === 'existing' && !choice.projectId) return setError('Pick a project to add the capture to.');
    if (!withSteps && !withApp) return setError('Pick what to add.');
    const signal = nextSignal();
    setError(null);
    try {
      let published: App | null = null;
      if (withApp) {
        setStep('publishing');
        published = needsFix
          ? await captureService.publishAppAndWait(captureId, signal, { anyway: true })
          : await captureService.publishAppAndWait(captureId, signal);
      }
      setStep('opening');
      const projectId = choice.mode === 'new' ? await createProject(name) : choice.projectId;
      if (withSteps) {
        setStep('steps');
        await captureService.importToProject(captureId, { projectId, frameIndices: stepIndices });
      }
      if (published) await captureService.attachApp(projectId, published.id);
      await navigate({ to: '/chat/$conversationId/video-workspace', params: { conversationId: projectId } });
    } catch (e) {
      if (signal.aborted) return;
      setError(apiErrorMessage(e));
      setStep(null);
    }
  }

  return (
    <Dialog open onOpenChange={open => !open && !step && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderPlus className="h-4 w-4 text-primary" />
            Add the capture to a project
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <fieldset className="space-y-2">
            <legend className="text-xs font-medium text-foreground mb-1">What goes in</legend>
            <label className={cn('flex items-start gap-2 text-sm', !stepIndices.length && 'opacity-60')}>
              <input type="checkbox" className="accent-primary mt-0.5" checked={withSteps} disabled={!stepIndices.length || !!step} onChange={e => setWithSteps(e.target.checked)} />
              <span>
                {stepIndices.length} step{stepIndices.length === 1 ? '' : 's'}
                <span className="block text-xs text-muted-foreground">Each step's screenshot and what was on screen, for scenes built from screenshots.</span>
              </span>
            </label>
            <label className={cn('flex items-start gap-2 text-sm', app !== 'built' && 'opacity-60')}>
              <input type="checkbox" className="accent-primary mt-0.5" checked={withApp} disabled={app !== 'built' || !!step} onChange={e => setWithApp(e.target.checked)} />
              <span>
                The replay, as an app
                <span className="block text-xs text-muted-foreground">
                  {app === 'built'
                    ? 'Published as an app that keeps working after the capture is rebuilt or deleted, for live scenes.'
                    : app === 'building'
                      ? 'Still building. Add it from the Replay tab when it is ready.'
                      : app === 'unavailable'
                        ? "Replays aren't available on this server yet."
                        : 'This capture has no replay.'}
                </span>
              </span>
            </label>
          </fieldset>
          {withApp && needsFix && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              This replay needs a fix{needsFix[0] ? `: ${needsFix[0]}` : ''}. A demo built on it shows its faults.
            </p>
          )}
          <ProjectChoiceFields value={choice} onChange={setChoice} projects={projects} />
          {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
        </div>

        <DialogFooter className="gap-2">
          <button
            onClick={onClose}
            disabled={!!step}
            className="h-9 rounded-lg border border-border bg-card px-4 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={add}
            disabled={!!step}
            className={cn(
              'h-9 inline-flex items-center gap-1.5 rounded-lg px-4 text-sm font-semibold transition',
              'bg-primary text-primary-foreground hover:bg-primary/90',
              'shadow-[0_0_0_1px_hsl(var(--primary)/0.25),0_6px_18px_hsl(var(--primary)/0.25)]',
              'disabled:opacity-60 disabled:cursor-not-allowed',
            )}
          >
            {step ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FolderPlus className="h-3.5 w-3.5" />}
            {step ? STEP_LABEL[step] : 'Add to project'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
